import type { WorkspaceIntegrationType } from "@storyboard/web-client";
import type { DriverRouteSummary } from "./driverRoute";

export type DriverViewTab = "my" | "all";

export type DriverSortKey = "alphaAsc" | "alphaDesc" | "most-recent" | "unread";

/**
 * Core driver roster columns the search filter may match (SQL-backed in the driver repository).
 *
 * `samsaraUsername` and `samsaraVehicleName` match workspace tag values whose `tag.label` matches the
 * default labels from `SamsaraUsername` / `SamsaraVehicleName` in `@storyboard/tags` (Samsara username
 * also allows a collapsed lowercase+no-whitespace match to `samsarausername`).
 */
export const DRIVER_SEARCH_COLUMNS = [
  "name",
  "alias",
  "group",
  "location",
  "samsaraUsername",
  "samsaraVehicleName",
] as const;
export type DriverSearchColumn = (typeof DRIVER_SEARCH_COLUMNS)[number];

/** `tag.label` for {@link DriverSearchColumn} `samsaraUsername`. */
export const DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL = "Username" as const;
/** `tag.label` for {@link DriverSearchColumn} `samsaraVehicleName`. */
export const DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL = "Vehicle name" as const;

/**
 * `tag.label` of the workspace tag promoted ahead of the built-in "Driver" column when present:
 * the column becomes the frozen leading column and inherits the global driver sort dropdown.
 *
 * Mirrors `SamsaraUsername.defaultLabel` in `@storyboard/tags`. Bootstrap does not expose
 * `WorkspaceIntegrationTagConfig.source` yet, so promotion matches on the default label and a small
 * set of legacy display names and labels that collapse to `samsarausername`; other custom labels are skipped
 * until source is plumbed.
 */
export const SAMSARA_USERNAME_TAG_LABEL = DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL;

/** Additional `tag.label` values used in seeded or renamed workspaces before source is available. */
const SAMSARA_USERNAME_LEGACY_PROMOTION_TAG_LABELS = ["Samsara username", "Driver username"] as const;

function collapsedLowercaseLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, "");
}

function isCollapsedSamsaraUsernameLabel(label: string): boolean {
  return collapsedLowercaseLabel(label) === "samsarausername";
}

/** Whether `tag` is the workspace Samsara username column for driver-view promotion and search. */
export function isSamsaraUsernameTag(tag: Pick<TagDefinition, "label">): boolean {
  return (
    tag.label === SAMSARA_USERNAME_TAG_LABEL ||
    (SAMSARA_USERNAME_LEGACY_PROMOTION_TAG_LABELS as readonly string[]).includes(tag.label) ||
    isCollapsedSamsaraUsernameLabel(tag.label)
  );
}

/** Whether `tag` is the workspace vehicle-name column, which carries the unit number of the truck a driver runs. */
export function isSamsaraVehicleNameTag(tag: Pick<TagDefinition, "label">): boolean {
  return tag.label === DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL;
}

/**
 * A single dimension the driver-view search may match: a core column or a workspace tag value (local tag id).
 */
export type DriverSearchTarget = { kind: "column"; field: DriverSearchColumn } | { kind: "tag"; tagId: string };

export const DRIVER_STATUS_FILTERS = [
  "driving",
  "not-driving",
  "hands-free-off",
  "volume-muted",
  "offline",
  "personal-device",
  "never-logged-in",
] as const;
export type DriverStatusFilter = (typeof DRIVER_STATUS_FILTERS)[number];

/**
 * Device Volume column buckets, evaluated against the displayed rounded percent
 * `round(mostRecentVolumeVolume * 100)`. Buckets are non-overlapping; `muted` matches
 * `mostRecentVolumeAudible = false` or a non-muted device rounding to 0%.
 */
export const DRIVER_VOLUME_FILTERS = ["100", "75-99", "50-74", "25-49", "1-24", "muted"] as const;
export type DriverVolumeFilter = (typeof DRIVER_VOLUME_FILTERS)[number];

/**
 * Duty status values mirror the `DutyStatus` enum in the Prisma schema
 * (`sst/packages/db/src/prisma/schema.prisma`) and are fed through App Sync
 * `membershipAvailabilities` into `wsMembershipAvailability.mostRecentDutyStatusStatus`.
 */
export const DRIVER_DUTY_STATUS_FILTERS = [
  "driving",
  "onDuty",
  "offDuty",
  "sleeperBed",
  "personalConveyance",
  "yardMove",
  "unknown",
] as const;
export type DriverDutyStatusFilter = (typeof DRIVER_DUTY_STATUS_FILTERS)[number];

export interface LocationFilter {
  latitude: number;
  longitude: number;
  radius: number;
  unit: "miles" | "km";
}

export interface DriverFilterConfig {
  tab: DriverViewTab;
  team: string;
  teamAliases: string[];
  sort: DriverSortKey;
  statusFilter: DriverStatusFilter[];
  volumeFilter: DriverVolumeFilter[];
  dutyStatusFilter: DriverDutyStatusFilter[];
  groupIds: string[];
  aliasNames: string[];
  location: LocationFilter | null;
  search: string;
  /** Search dimensions; empty disables search regardless of `search`. */
  searchTargets: readonly DriverSearchTarget[];
  language: string;
  unactivatedMembershipIds: string[];
  /** Memberships currently registered on at least one personal-mode device; also drives the row indicator. */
  personalDeviceMembershipIds: string[];
}

/**
 * What a Dispatch driver row's Message cell shows: the driver's message verbatim, the driver's photo described as
 * {@link PHOTO_MESSAGE_TEXT}, or a required TMS event.
 */
export type DriverRecentSource = "message" | "photo" | "tmsEvent";

/** What a Dispatch row shows for a driver's photo in place of its generated file name. */
export const PHOTO_MESSAGE_TEXT = "Sent a photo";

export interface DriverRow {
  accountId: string | null;
  workspaceMembershipId: string;
  feedId: string | null;
  workspaceId: string;
  name: string | null;
  alias: string | null;
  recentMessage: string | null;
  group: string | null;
  groupId: string | null;
  location: string | null;
  country: string | null;
  stateProvince: string | null;
  volume: number | null;
  feedPrefId: string | null;
  unread: boolean | null;
  calculatedAvailability: string | null;
  offline: boolean | null;
  mostRecentDrivingStatus: string | null;
  mostRecentDrivingStatusChangedAt: string | null;
  mostRecentDutyStatusStatus: string | null;
  mostRecentDutyStatusChangedAt: string | null;
  mostRecentHandsFreeStatusEnabled: boolean | null;
  mostRecentHandsFreeStatusUpdatedAt: string | null;
  mostRecentVolumeAudible: boolean | null;
  membershipAvailabilityUpdatedAt: string | null;
  latitude: number | null;
  longitude: number | null;
  /** When the location was recorded, the availability row's ISO string; every surface judges staleness from it. */
  locationUpdatedAt: string | null;
  /** The ELD the location came from; null for the phone. */
  locationSource: WorkspaceIntegrationType | null;
  /** The place name the location's source gave, e.g. "Amarillo, TX"; null when it gave none. */
  locationPlaceName: string | null;
  tags: Record<string, string[]>;
  /** Merged in from the device-usage endpoint rather than the roster query; false until that fetch lands. */
  hasPersonalDevice: boolean;
  /**
   * ISO time of what the row's Time and Message show. On the Dispatch rows, the driver's latest alias-feed message or
   * a newer required TMS event; on the driver-view rows, the latest message alone. Absent on synthetic rows.
   */
  latestItemCreatedAt?: string | null;
  /** Whether `recentMessage` is the driver's message or a required TMS event; Dispatch rows only, null when empty. */
  recentSource?: DriverRecentSource | null;
  /** The driver's current route; carried by the Dispatch rows (`null` on no route), absent on driver-view rows. */
  route?: DriverRouteSummary | null;
  /** Whether the driver reads as never logged in (`isNeverLoggedIn`); carried by the Dispatch rows alone. */
  neverLoggedIn?: boolean;
}

export interface TagDefinition {
  id: string;
  /** Immutable, workspace-unique natural key; Dispatch resolves `tag:<slug>` view refs to `id` through it. */
  slug: string;
  label: string;
  multiValue: boolean;
}
